// Start ohne Server (docs/kasse-offline/SPEZIFIKATION.md §3.4, §14.7): Scheitert die Sitzung beim Start ohne Antwort
// von Merxo (network, timeout, foreign), fragt die Hülle die Proben der Module (Moduloption `offlineStart`). Liefert
// eine einen Kontext, zeigt die Hülle nur deren Vollbild-Route mit einer Ersatz-Sitzung (web/stores/session.ts) – gleich,
// auf welcher Route die Anwendung gestartet ist (auch `/`, die start_url der installierten Web-App). Alle anderen Seiten
// bleiben ohne Server gesperrt. Antwortet Merxo (Kante 5xx, Server 5xx), gibt es KEINEN Start ohne Server: Das ist eine
// Störung, keine fehlende Verbindung (§0 O1).
//
// Bei der ersten erfolgreichen Gesundheitsprüfung lädt die Hülle die echte Sitzung nach – ohne Neuladen.
import { useEffect } from 'react'
import { matchRoutes } from 'react-router'
import { getApiClient } from '../api/client'
import type { OfflineStartContext, OfflineStartProbe, WebModule, WebRoute } from '../module'
import { resumeSession, type OfflineStart } from '../stores/session'

/** so lange wartet die Hülle höchstens auf eine Probe (IndexedDB kann bei einem Versionswechsel hängen, §2.3) */
export const OFFLINE_PROBE_TIMEOUT_MS = 3_000
/** Gesundheitsprüfung, solange die Ersatz-Sitzung gilt: 5 s → 10 s → 30 s, dann alle 30 s (§10.1) */
export const OFFLINE_RECONNECT_MS: readonly number[] = [5_000, 10_000, 30_000]

/**
 * Nur im Testaufbau (`vite build --mode e2e`, e2e/foundation/offline-start.spec.ts): eine Test-Probe, die der Ablauf
 * vor dem Laden setzt. Im gewöhnlichen Bau ist der Zweig entfernt (MODE ist zur Bauzeit fest).
 */
const E2E_PROBE_KEY = '__merxoOfflineStartProbe'
// Kennzeichen für den Ablauf: Dieser Bau nimmt die Test-Probe an (fehlt es, überspringt der Ablauf mit Hinweis)
if (import.meta.env.MODE === 'e2e' && typeof document !== 'undefined') document.documentElement.dataset.offlineProbe = 'e2e'

interface ProbeEntry {
  /** Modul der Probe; null = Test-Probe (darf in die Vollbild-Route jedes Moduls führen) */
  module: string | null
  load: () => Promise<OfflineStartProbe>
}

const isText = (value: unknown, max = 500): value is string => typeof value === 'string' && value.length > 0 && value.length <= max

/**
 * Darf eine Probe hierhin führen? Eigener Pfad (beginnt mit einem `/`, kein `//`, kein Suchteil) und – nach der
 * Rangfolge des Routers über ALLE Module – eine Route mit `layout: 'fullscreen'` des Moduls der Probe (null = Test-Probe:
 * eines beliebigen Moduls). So zeigt der Start ohne Server nie den App-Rahmen mit Navigation.
 */
export function offlineStartRoute(path: unknown, modules: readonly WebModule[], module: string | null): WebRoute | null {
  if (!isText(path, 2_000) || !path.startsWith('/') || path.startsWith('//') || /[?#\\]/.test(path)) return null
  const routes = modules.flatMap((entry) => entry.routes.map((route) => ({ path: route.path, handle: { module: entry.id, route } })))
  const best = matchRoutes(routes, path)?.[0]?.route.handle
  return best?.route.layout === 'fullscreen' && (module === null || best.module === module) ? best.route : null
}

export const isOfflineStartPath = (path: unknown, modules: readonly WebModule[], module: string | null): path is string =>
  offlineStartRoute(path, modules, module) !== null

/** Kontext prüfen – was die Probe liefert, wird zur Sitzung; eine Platzhalter-Berechtigung ('*') gibt es dort nie. */
export function isOfflineStartContext(value: unknown): value is OfflineStartContext {
  if (!value || typeof value !== 'object') return false
  const context = value as Partial<OfflineStartContext>
  const { user, tenant, location, permissions, features, returnLabel } = context
  if (!user || !isText(user.id) || !isText(user.displayName)) return false
  if (!tenant || !isText(tenant.id) || !isText(tenant.name) || typeof tenant.demo !== 'boolean') return false
  if (!location || !isText(location.id) || typeof location.code !== 'string' || !isText(location.name)) return false
  if (permissions !== undefined) {
    if (!Array.isArray(permissions)) return false
    if (!permissions.every((key) => isText(key, 200) && !key.includes('*') && !key.startsWith('@'))) return false
  }
  if (features !== undefined && !(Array.isArray(features) && features.every((key) => isText(key, 200)))) return false
  if (returnLabel !== undefined && !isText(returnLabel, 80)) return false
  return true
}

function probesOf(modules: readonly WebModule[]): ProbeEntry[] {
  const entries: ProbeEntry[] = modules.flatMap((module) =>
    module.offlineStart ? [{ module: module.id, load: async () => (await module.offlineStart!.lazy()).default }] : [],
  )
  if (import.meta.env.MODE === 'e2e') {
    const probe = (globalThis as Record<string, unknown>)[E2E_PROBE_KEY]
    if (typeof probe === 'function') {
      entries.push({ module: null, load: async () => probe as OfflineStartProbe })
    }
  }
  return entries
}

async function ask(entry: ProbeEntry, modules: readonly WebModule[], timeoutMs: number): Promise<OfflineStart | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs)
  })
  try {
    const answer = await Promise.race([entry.load().then((probe) => probe()), late])
    if (!answer || typeof answer !== 'object') return null
    const { path, context } = answer as { path?: unknown; context?: unknown }
    const route = offlineStartRoute(path, modules, entry.module)
    if (!route || !isOfflineStartContext(context)) {
      if (import.meta.env.DEV) console.warn(`[Start ohne Server] Die Probe von ${entry.module || 'Test'} lieferte keinen gültigen Kontext.`)
      return null
    }
    // ohne Angabe: genau das Recht und der Funktionsschalter der Route – keine anderen
    const normalized: OfflineStartContext = {
      ...context,
      permissions: context.permissions ?? (route.permission ? [route.permission] : []),
      features: context.features ?? (route.feature ? [route.feature].flat() : []),
    }
    return { module: entry.module ?? '', path: path as string, context: normalized }
  } catch (error) {
    if (import.meta.env.DEV) console.warn(`[Start ohne Server] Die Probe von ${entry.module || 'Test'} ist gescheitert.`, error)
    return null
  } finally {
    clearTimeout(timer)
  }
}

/** Fragt alle Proben gleichzeitig; es gilt die erste in der Reihenfolge der Module, die einen gültigen Kontext liefert. */
export async function probeOfflineStart(
  modules: readonly WebModule[],
  options: { timeoutMs?: number } = {},
): Promise<OfflineStart | null> {
  const entries = probesOf(modules)
  if (entries.length === 0) return null
  const answers = await Promise.all(entries.map((entry) => ask(entry, modules, options.timeoutMs ?? OFFLINE_PROBE_TIMEOUT_MS)))
  return answers.find((answer) => answer !== null) ?? null
}

/**
 * Solange die Ersatz-Sitzung gilt: Gesundheitsprüfung nach OFFLINE_RECONNECT_MS, sofort beim `online`-Ereignis und beim
 * Wechsel in den Vordergrund; bei der ersten Antwort `ok` die echte Sitzung nachladen (ohne Neuladen der Seite).
 */
export function useOfflineReconnect(active: boolean): void {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    let stopped = false
    let running = false
    let attempt = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = (): void => {
      clearTimeout(timer)
      const delay = OFFLINE_RECONNECT_MS[Math.min(attempt, OFFLINE_RECONNECT_MS.length - 1)] as number
      attempt += 1
      timer = setTimeout(() => void check(), delay)
    }
    const check = async (): Promise<void> => {
      if (stopped || running) return
      running = true
      clearTimeout(timer)
      try {
        const health = await getApiClient().health().catch(() => null)
        if (!stopped && health?.reason === 'ok' && (await resumeSession())) return
      } finally {
        running = false
      }
      if (!stopped) schedule()
    }
    const now = (): void => {
      attempt = 0
      void check()
    }
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') now()
    }
    schedule()
    window.addEventListener('online', now)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped = true
      clearTimeout(timer)
      window.removeEventListener('online', now)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [active])
}
